import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { animateScroll as scroll } from 'react-scroll/modules';

import { Input } from '@eika/input';

import { FormTypeCompletionDetails, UpdateConsent } from '../../../actions';
import { DispatchWithState, StringKeyValue } from '../../../actions/types';
import ConsentList from '../../../components/Consent/ConsentList';
import ErrorMessage from '../../../components/Form/ErrorMessage';
import StepButtons from '../../../components/Steps/StepButtons';
import { CalculatorReducer } from '../../../reducers/calculator';
import { CompletionReducer } from '../../../reducers/completion';
import { ConsentReducer } from '../../../reducers/consent';
import { VehicleReducer } from '../../../reducers/vehicle';
import { loanCompletionDetails } from '../../../validation/loanCompletionSchema';

interface Props {
    action: {
        loanCompletionDetailsNextEvent: (form: FormTypeCompletionDetails) => DispatchWithState;
        updateConsent: (name: UpdateConsent['name'], value: UpdateConsent['value']) => void;
        openStep: (step: number) => void;
    };
    completion: CompletionReducer;
    consent: ConsentReducer;
    calculator: CalculatorReducer;
    vehicle: VehicleReducer;
    text: StringKeyValue;
    showConsentPage: boolean;
}

const LoanCompletionDetails = (props: Props): JSX.Element => {
    const { action, completion, text, consent, showConsentPage } = props;
    const { loanCompletionDetailsNextEvent, openStep } = action;
    const { ongoing } = completion;
    const { customerIsMainApplicant } = ongoing;
    const { completionDetailsContactInfoTitle } = text;
    useEffect(() => {
        scroll.scrollToTop();
        // Skip the details step entirely for the main applicant when there is no consent page to show.
        if (!showConsentPage && customerIsMainApplicant) {
            action.openStep(5);
        }
    }, [action, customerIsMainApplicant, showConsentPage]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormTypeCompletionDetails>({
        resolver: yupResolver(loanCompletionDetails),
        shouldUnregister: true,
        mode: 'all',
    });

    const hasError = Object.keys(errors).length > 0;
    const formError = <ErrorMessage message={text.formErrorMessage} />;

    return (
        <div className="loan-completion-details">
            <div className="loan-completion-details__form">
                <form
                    onSubmit={handleSubmit((form) => {
                        loanCompletionDetailsNextEvent(form);
                    })}
                >
                    {!customerIsMainApplicant && (
                        <>
                            <h3>{completionDetailsContactInfoTitle}</h3>
                            <div className="loan-completion-details__email input-wrapper">
                                <Input
                                    {...register('email')}
                                    type="email"
                                    label={text.applicantLabelEmail}
                                    defaultValue={ongoing.email}
                                    error={String(errors?.email?.message || '')}
                                />
                            </div>

                            <div className="loan-completion-details__phone input-wrapper">
                                <Input
                                    {...register('phone')}
                                    type="tel"
                                    label={text.applicantLabelPhone}
                                    defaultValue={ongoing.phone}
                                    error={String(errors?.phone?.message || '')}
                                    maxLength={8}
                                />
                            </div>
                        </>
                    )}

                    {showConsentPage && (
                        <ConsentList text={text} consent={consent} updateConsent={action.updateConsent} />
                    )}

                    {hasError && formError}

                    <StepButtons showPrevious previousChangeEvent={() => openStep(1)} nextButtonType="submit" />
                </form>
            </div>
        </div>
    );
};

export default LoanCompletionDetails;
